import React, { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isToday } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import Table from 'react-bootstrap/Table';
import logger from "../../utils/logger";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faPenToSquare, faTimesCircle, faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faTimes, faTrashCan, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';

import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';

import { ActivityIndicator } from '../custom/ActivityIndicator';
import { getTableHeaderStyles, getStatusCellStyles, removeUnderscore } from '../lib/functions';
import AddServiceModal from '../custom/AddServiceModal'
import { v4 as uuidv4 } from 'uuid';

import "../css/Calendar.css";

import {
    fetch_all_service_records,
    use_add_new_service,
    use_delete_service,
    use_update_service,
    use_fetch_pdf,
    use_update_service_status,
    use_update_service_future_schedule,
    use_toggle_inService,
    use_toggle_vor,
    use_remove_comment,
    use_add_comment,
} from "../../api/service";

import { use_update_service_dates } from "../../api/vehicles";
import { UploadPdf } from "./UploadPdf";
import PdfViewer from "./PDFViewer";

const AdvancedCalendar = ({ theme, selectedVehicle, allVehicles, serviceCompanies, showServiceRecords, filterCompanyId }) => {
    // selectedVehicle is the vehicle id that is selected by the user to highlight the dates with service records
    // showServiceRecords is a boolean to show the service records instead of the calendar

    const { showAlert } = useAlert();
    const { isAdmin, isManager, isDev, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_services_edit;

    const [isLoading, setIsLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [draggingEvent, setDraggingEvent] = useState(null);
    const [serviceData, setServiceData] = useState([]);
    const [openModal, setOpenModal] = useState(false);

    const [pdfUploadData, setPdfUploadData] = useState(null);
    const [showUploadPdfModal, setShowUploadPdfModal] = useState(false);

    const [expandedCommentId, setExpandedCommentId] = useState(null);

    const [showMotDateSelector, setShowMotDateSelector] = useState(false);
    const [selectedMotDate, setSelectedMotDate] = useState("");
    const [motEventForSelector, setMotEventForSelector] = useState(null);

    // Filter service data by company when a filter is active
    const displayServiceData = filterCompanyId
        ? serviceData.filter(event => event.service_company_id === filterCompanyId)
        : serviceData;

    // state to store selected vehicle service records
    const [selectedVehicleServiceRecords, setSelectedVehicleServiceRecords] = useState([]);

    // DEBUG =======================================================================================================================


    // DEBUG =======================================================================================================================

    // Fetching service data
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [serviceResponse] = await Promise.all([
                fetch_all_service_records(),
            ]);

            const serviceResponseData = serviceResponse?.data ?? null;
            setServiceData(serviceResponseData?.data ?? []);
        } catch (error) {
            logger.error("Error fetching data:", error);
        } finally {
            setTimeout(() => setIsLoading(false), 1000);
        }
    };

        const getEventIndicators = (event) => [
            (event.service || event.brakes) && { type: 'pmi', label: 'PMI' },
            event.mot && { type: 'mot', label: 'MOT' },
            event.repair && { type: 'repair', label: 'Repair' },
            event.loler && { type: 'loler', label: 'LOLER' },
            event.tacho && { type: 'tacho', label: 'Tacho' },
        ].filter(Boolean);

    // Trigger the fetchData function on component mount
    useEffect(() => {
        fetchData();
    }, []);

    // filter and sort service records for selected vehicle ===============================================================================================
    useEffect(() => {
        if (!selectedVehicle) return;
    
        const filteredServiceRecords = serviceData
            .filter((record) => String(record.vehicle_id) === String(selectedVehicle))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort in descending order
    
        setSelectedVehicleServiceRecords(filteredServiceRecords);
    }, [selectedVehicle, serviceData]);
    

    // check if the day has any service records for selected vehicle =======================================================================================
    const hasService = (dayEvents, selectedVehcicle) => {
        return dayEvents.some((event) => String(event.vehicle_id) === String(selectedVehcicle));
    };

    // Check if the service is outstanding =================================================================================================================
    const isOutstanding = (currentDate, dayEvents) => {

        // Return false if dayEvents is empty
        if (!Array.isArray(dayEvents) || dayEvents.length === 0) return false;
    
        const current = new Date(currentDate);
    
        return dayEvents.some(event => {
            const eventDate = new Date(event.date);
            
            // Return false if the current date is before the event date
            if (current < eventDate) return false;
    
            return (
                    (event.mot === true && event.mot_completed === false) ||
                    (event.service === true && event.service_completed === false) ||
                    (event.brakes === true && event.brakes_completed === false) ||
                    (event.tacho === true && event.tacho_completed === false)
            );
        });
    }

    const isInService = (currentDate, dayEvents) => {
        if (!Array.isArray(dayEvents) || dayEvents.length === 0) return false;
    
        const current = new Date(currentDate);
    
        // Filter out events that are in the future
        const relevantEvents = dayEvents.filter(event => new Date(event.date) <= current);
    
        if (relevantEvents.length === 0) return false;
    
        // First check: if any event is outstanding but NOT in for service, return false
        const hasOutstandingNotInService = relevantEvents.some(event => {
            return (
                (event.mot && !event.mot_completed && !event.inForService) ||
                (event.service && !event.service_completed && !event.inForService) ||
                (event.brakes && !event.brakes_completed && !event.inForService) ||
                (event.tacho && !event.tacho_completed && !event.inForService)
            );
        });
    
        if (hasOutstandingNotInService) return false;
    
        // Second check: if any event is outstanding and in for service, return true
        const hasOutstandingInService = relevantEvents.some(event => {
            return (
                (event.mot && !event.mot_completed && event.inForService) ||
                (event.service && !event.service_completed && event.inForService) ||
                (event.brakes && !event.brakes_completed && event.inForService) ||
                (event.tacho && !event.tacho_completed && event.inForService)
            );
        });
    
        if (hasOutstandingInService) return true;
    
        // If none of the above conditions are met, return false
        return false;
    };
    


    // Generate the calendar ===============================================================================================================================
    const generateCalendar = () => {
        const startDate = startOfWeek(startOfMonth(currentDate));
        const endDate = endOfWeek(endOfMonth(currentDate));
        const days = [];
        let day = startDate;

        while (day <= endDate) {
            const formattedDate = format(day, "yyyy-MM-dd");
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const today = new Date();
            const dayEvents = displayServiceData?.filter((event) => format(new Date(event.date), "yyyy-MM-dd") === formattedDate) ?? [];
            
            days.push(
                <motion.div
                    key={formattedDate}
                    className={`day ${isCurrentMonth ? "current-month" : "other-month"} ${
                        isToday(day) ? "today" : ""
                    }`}
                    onClick={() => setSelectedDate(formattedDate)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(formattedDate)}
                    whileHover={{ scale: 1.05 }}
                    style={{
                        height: '12.5vh',
                        zIndex: 0,
                        background: hasService(dayEvents, selectedVehicle) 
                            ? 'rgba(240,167,0,0.7)' // Color for when there's a service
                            : null,
                        border: isInService(today, dayEvents) ? '2px solid rgb(236, 193, 1)' : isOutstanding(today, dayEvents) ? '2px solid red' : null,
                    }}
                >
                    <div className="date">{format(day, "d")}</div>
                    <div className="event-container"
                        style={{
                            height: '100%',
                            overflowY: 'auto',
                            padding: 5,
                            paddingBottom: 25,
                            borderRadius: 5,
                        }}
                    >
                    {dayEvents.map((event, index) => (
                        <motion.div
                            key={index}
                            className="event"
                            draggable
                            onDragStart={() => setDraggingEvent({ event })}
                            whileDrag={{ opacity: 0.8 }}
                            style={{ backgroundColor: event.type === 'TRAILER' ? 'rgba(178, 4, 194, 0.7)' : null }}
                        >
                            <span className="event-label">{event.reg}</span>
                            <span className="event-indicators">
                                {getEventIndicators(event).map((indicator) => (
                                    <span
                                        key={indicator.type}
                                        className={`event-indicator event-indicator-${indicator.type}`}
                                        title={indicator.label}
                                        aria-label={indicator.label}
                                    />
                                ))}
                            </span>
                        </motion.div>
                    ))}
                    </div>
                </motion.div>
            );
            day = addDays(day, 1);
        }
        return days;
    };

    // Update future schedule ==============================================================================================================
    const updateFutureSchedule = async (service_data) => {
        const { message, status } = await use_update_service_future_schedule(service_data);
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        }
        fetchData(); // refetch data (refresh the calendar)
    };

    // Confirm and Add new MOT date manually instead of failing future schedule updates
    const handleConfirmMotDate = async () => {
        if (!selectedMotDate || !motEventForSelector) return;
        
        setIsLoading(true);
        setShowMotDateSelector(false);
        
        const newMotData = {
            date: selectedMotDate,
            vehicle_id: motEventForSelector.vehicle_id,
            service_company_id: motEventForSelector.service_company_id,
            service_uuid: uuidv4(),
            service: false,
            brakes: false,
            mot: true,
            tacho: false,
            loler: false,
            repair: false,
        };
        
        const { message, status } = await use_add_new_service(newMotData);
        setIsLoading(false);
        setMotEventForSelector(null);
        
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message || "Failed to add new MOT record.",
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        } else {
            showAlert({
                message: "New MOT event successfully added for this vehicle!",
                type: 'success',
                buttons: [{
                    label: 'Close',
                }],
            });
        }
        fetchData(); // refresh the calendar
    };

    // Handling event drag and drop ========================================================================================================
    const handleDrop = async (targetDate) => {
        if (!draggingEvent) return;
    
        const { event } = draggingEvent;
        const uuid = event.service_uuid;
        const sourceDate = event.date;
    
        if (sourceDate === targetDate) return;
    
        // Update serviceData array
        setServiceData((prev) => {
            // Filter out the event from the source date
            const updatedEvents = prev.filter((event) => event.service_uuid !== uuid);
    
            // Add the event to the target date with the updated date
            const updatedEvent = { ...event, date: targetDate };
            updatedEvents.push(updatedEvent);
    
            return updatedEvents;
        });
    
        // Call your service update route here
        const vehicle = allVehicles.find((vehicle) => vehicle.id === event.vehicle_id);

        const service_data = {
            service_uuid: event.service_uuid,
            date: targetDate,
            vehicle_type: vehicle.type,
            year: vehicle.year_manufactured
        };
    
        const { message, status } = await use_update_service(service_data); // Implement the logic for service update
    
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                    onClick: () => {
                        setDraggingEvent(null);
                    },
                }],
            });
        } else {
            showAlert({
                message: `Event moved, Adjust future dates?`,
                type: 'success',
                buttons: [
                    {
                        label: 'Yes',
                        type: 'primary',
                        onClick: () => {
                            setDraggingEvent(null);
                            if (event.mot) {
                                setMotEventForSelector(event);
                                setSelectedMotDate(targetDate); // Prefill with new drop date
                                setShowMotDateSelector(true);
                            } else {
                                // Call the service update route to adjust future dates
                                updateFutureSchedule(service_data);
                            }
                        }
                    },
                    {
                        label: 'No',
                        type: 'danger',
                        onClick: () => {
                            setDraggingEvent(null);
                        },
                    }
                ],
            });
        }
    };
    
    

    // Handle adding new service ========================================================================================================
    const handleAddService = async (data) => {
        try {

            logger.log("Add Service Data:", data);

            const vehicle = allVehicles.find((vehicle) => vehicle.id === Number(data.selectedVehicle));

            // format the data to match the API request
            const formattedData = {
                date: selectedDate,
                vehicle_id: data.selectedVehicle,
                service_company_id: data.selectedServiceContract,
                service_uuid: uuidv4(),
                service: data.serviceTypes.includes('PMI / BRAKES'),
                brakes: data.serviceTypes.includes('PMI / BRAKES'),
                mot: data.serviceTypes.includes('MOT'),
                tacho: data.serviceTypes.includes('TACHO'),
                loler: data.serviceTypes.includes('LOLER'),
                repair: data.serviceTypes.includes('REPAIR'),
            }

            // update schedule data
            const service_data = {
                service_uuid: formattedData.service_uuid,
                date: selectedDate,
                vehicle_type: vehicle.type,
                year: vehicle.year_manufactured
            };

            const { message, status } = await use_add_new_service(formattedData);  // Call the add_service function with the required data
            logger.log("Add Service Response:", message, status);

            if (status !== 200) {
                showAlert({
                    message: message,
                    type: 'danger',
                    buttons: [{
                        label: 'Close',
                    }],
                    onClick: () => {
                        setSelectedDate(null);
                        fetchData(); // refetch data (refresh the calendar)
                    }
                });
            } else {
                showAlert({
                    message: `Event added, set future dates?`,
                    type: 'success',
                    buttons: [
                        {
                            label: 'Yes',
                            type: 'primary',
                            onClick: () => {
                                if (formattedData.mot) {
                                    setMotEventForSelector(formattedData);
                                    setSelectedMotDate(selectedDate);
                                    setShowMotDateSelector(true);
                                } else {
                                    // Call the service update route to adjust future dates
                                    updateFutureSchedule(service_data);
                                }
                                setSelectedDate(null);
                            }
                        },
                        {
                            label: 'No',
                            type: 'danger',
                            onClick: () => {
                                setSelectedDate(null);
                                fetchData(); // refetch data (refresh the calendar)
                            },
                        }
                    ],
                });
            }
        } catch (error) {
            logger.error("Error adding service:", error);
        }
    };

    // Check if the event has a PDF uploaded ==============================================================================================================
    const checkForPdf = (event, type) => {
        // default true if repair
        if (type === 'repair') {
            return true;
        }
        const pdfColumn = type + '_pdf';
        return event[pdfColumn] ? true : false;
    };

    // Check the status of the service ======================================================================================================================
    const checkStatus = (event, type) => {
        // default true for repairs
        if (type === 'repair') {
            return true;
        }
        const statusColumn = type + '_completed';
        return event[statusColumn] ? true : false;
    };

    // Check has event ======================================================================================================================================
    const hasEvent = (event, type) => {
        // Check if the event has the type of service
        return event[type] ? true : false;
    };

    // Check if all services are completed ==================================================================================================================
    const checkCompleted = (event) => {
        // Check if each service is required and if it is, check if it's completed
        // Note: 'repair' is excluded from completion checking as per requirements
        if (event.mot && !event.mot_completed) return false;
        if (event.service && !event.service_completed) return false;
        if (event.brakes && !event.brakes_completed) return false;
        if (event.tacho && !event.tacho_completed) return false;
        if (event.loler && !event.loler_completed) return false;
    
        // If no service is incomplete, return true
        return true;
    };
    

    // Open the PDF in a new tab ==========================================================================================================================
    const [pdfUrl, setPdfUrl] = useState(null);
    const [showPdf, setShowPdf] = useState(false);

    const handleOpenPdf = async (event, type) => {
        const pdfColumn = type + '_pdf';

        const response = await use_fetch_pdf(event[pdfColumn]); // Implement the fetch_pdf function

        setPdfUrl(response?.pdfUrl ?? null);

        if(response?.status !== 200  || response?.status !== 201) {
            showAlert({
                message: `Opening PDF ${event[pdfColumn]}`,
                type: 'success',
                buttons: [{
                    label: 'Open',
                    type: 'primary',
                    onClick: () => {
                        setShowPdf((prev) => !prev);
                    },
                }]
            });
        } else {
            showAlert({
                message: response?.message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                    type: 'danger',
                }]
            });
        }
    };

    // Handle service completion ==========================================================================================================================
    const handleComplete = async (uuid, type, reg) => {
        // find vehicle
        const vehicle = allVehicles.find((vehicle) => vehicle.reg === reg);
        const year_manufactured = vehicle.year_manufactured;
        const vehicle_type = vehicle.type;
        const service_type = type

        // ask the user if they want to complete the service
        showAlert({
            message: `Complete service ${type} for ${reg}`,
            type: 'success',
            buttons: [
                {
                    label: 'Complete',
                    type: 'primary',
                    onClick: () => {
                        // Call the service completion route
                        completeService(uuid, service_type, reg, vehicle_type, year_manufactured);
                    },
                },
                {
                    label: 'Cancel',
                    type: 'danger',
                }
            ],
        });
    };

    const completeService = async (uuid, service_type, reg, vehicle_type, year_manufactured) => {
        // update the database
        const { status } = await use_update_service_status({ service_uuid: uuid, type: service_type });

        if (status !== 200 && status !== 201) {
            // show an error message
            showAlert({
                message: `Error completing ${service_type} for ${reg}`,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                    type: 'danger',
                }],
            });
        } else {

            // update the dates for the vehicle
            const { status } = await use_update_service_dates({
                reg: reg,
                vehicle_type: vehicle_type,
                service_type: service_type,
                date: selectedDate,
                year_manufactured: year_manufactured
            });

            if (status !== 200 && status !== 201) {
                // show an error message
                showAlert({
                    message: `Error updating dates for ${reg}`,
                    type: 'danger',
                    buttons: [{
                        label: 'Close',
                        type: 'danger',
                        onClick: () => {
                            return;
                        }
                    }],
                });
            };
            // update the serviceData array
            setServiceData((prev) => {
                return prev.map((event) => {
                    if (event.service_uuid === uuid) {
                        event[service_type + '_completed'] = true;
                    }
                    return event;
                });
            });

            showAlert({
                message: `Upload a PDF for ${reg} ${service_type}`,
                type: 'success',
                buttons: [
                    {
                        label: 'Upload',
                        type: 'primary',
                        onClick: () => {
                            //handleUploadPdf(uuid, type)
                            setPdfUploadData({ uuid, type: service_type });
                            setShowUploadPdfModal(true); // Ensure modal opens
                            setSelectedDate(null);
                        },
                    },
                    {
                        label: 'No PDF',
                        type: 'danger',
                    }
                    ],
            });
        }
    };


    // Render the day view when a date is selected ========================================================================================================
    const renderDayView = () => {
        if (!selectedDate) return null; // If no date is selected, don't render anything
    
        // Filter events for the selected date
        const dayEvents = displayServiceData.filter((event) => format(new Date(event.date), "yyyy-MM-dd") === selectedDate);
    
        return (
            <motion.div
                className="service-modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                style={{ zIndex: 1 }}
            >
                <div className="modal-content">
                    <h2>{format(new Date(selectedDate), "MMMM dd, yyyy")}</h2>
    
                    {dayEvents.length > 0 ? (
                        <ul>
                            {dayEvents.map((event, index) => (
                                <li key={index}>
                                    <span className="modal-data" style={{color: event.vor ? '#ff2222' : ''}}>
                                        Vehicle: {event.reg}{event.type === 'TRAILER' && event.chassis ? ` - ${event.chassis}` : ''} {event.vor ? '[MARKED VOR]' : ''}
                                    </span>
                                    <span className="modal-data">
                                        Provider: {event.service_company} 
                                        {event.inForService
                                            ? `  [SERVICE PROVIDER HAS ${event.type === 'MHE' ? 'MHE' : 'VEHICLE'}]`
                                            : ''
                                        }
                                    </span>

                                    {['service', 'brakes', 'mot', 'tacho', 'loler', 'repair'].map((type) =>
                                        event[type] ? (
                                            <div
                                                key={type}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    height: 30,
                                                }}
                                            >
                                                <span
                                                    style={{ width: 24 }}
                                                    onClick={canEdit && !checkForPdf(event, type) ? () => handleComplete(event.service_uuid, type, event.reg) : null}
                                                >
                                                    <FontAwesomeIcon
                                                        style={{
                                                            width: 24,
                                                            cursor: canEdit && !checkForPdf(event, type) ? 'crosshair' : null,
                                                        }}
                                                        icon={type === "repair" ? faScrewdriverWrench : checkForPdf(event, type) ? faCheck : faClipboard}
                                                    />
                                                </span>

                                                <span
                                                    className="service-data"
                                                    style={{
                                                        color: type === 'repair' ? '#d600cbff' : checkStatus(event, type) ? 'green' : event.inForService ? 'yellow' : 'red',
                                                        marginLeft: '8px',
                                                        alignSelf: 'center',
                                                    }}
                                                >
                                                    {type === 'repair' ? 'Booked for repair' : checkStatus(event, type) ? 'Completed:' : 'Booked:'} {type === 'service' ? 'PMI' : type.charAt(0).toUpperCase() + type.slice(1)}
                                                </span>
                                            </div>
                                        ) : null
                                    )}
                                    {event.comments && (
                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                            <span
                                                className="modal-data"
                                                style={{ fontSize: 18, marginTop: 5, fontStyle: 'italic' }}
                                            >
                                                Comments: {event.comments}
                                            </span>
                                            <span>
                                                {canEdit && (
                                                <FontAwesomeIcon
                                                    icon={faTimesCircle}
                                                    size="lg"
                                                    style={{ color: 'red', width: 20, cursor: 'pointer', marginLeft: 10 }}
                                                    onClick={() => removeComment(event.service_uuid)}
                                                />
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    <div
                                        className="modal-buttons"
                                        style={{
                                            borderBottom:
                                                theme === 'dark' ? '1px solid #ccc' : '1px solid #333',
                                        }}
                                    >
                                        {canEdit && (
                                        <button
                                            className="btn btn-danger"
                                            style={{ margin: 5 }}
                                            onClick={() => toggleVor(event.service_uuid, event.vor, event.vehicle_id)}
                                        >
                                            VOR
                                        </button>
                                        )}
                                        {canEdit && !checkCompleted(event) && (
                                        <button
                                            className="btn btn-danger"
                                            style={{ margin: 5 }}
                                            onClick={() => toggleInService(event.service_uuid, event.inForService)}
                                        >
                                            In Service
                                        </button>)}
                                        {canEdit && (!event.comments) && (
                                        <button
                                            className="btn btn-danger"
                                            style={{ margin: 5 }}
                                            onClick={() => addComment(event.service_uuid)}
                                        >
                                            Comment
                                        </button>)}
                                        {canEdit && (
                                            <button
                                                className="btn btn-danger"
                                                style={{ margin: 5 }}
                                                onClick={() => deleteEvent(event.service_uuid)}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}

                        </ul>
                    ) : (
                        <p>No events for this date.</p>
                    )}
    
                    {canEdit && (
                    <button
                        className="btn btn-primary"
                        style={{ marginBottom: 10 }}
                        onClick={() => addEvent()}
                    >
                        Add Event
                    </button>
                    )}
                    <button
                        className="btn btn-secondary"
                        onClick={() => setSelectedDate(null)}
                    >
                        Close
                    </button>
                </div>
            </motion.div>
        );
    
    };

    // Add Event Button Clicked ========================================================================================================
    const addEvent = () => {
        // Show Add Event Modal
        setOpenModal(true)
    };

    // Delete Event ========================================================================================================
    const deleteEvent = async (uuid) => {
        const service_data = {
            service_uuid: uuid
        }
    
        // Call delete service route
        const { message, status } = await use_delete_service(service_data); // Implement delete service logic

        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        }

        // Update the serviceData array
        setServiceData((prev) => prev.filter((event) => event.service_uuid !== uuid));
        
        // close the selected date view
        setSelectedDate(null);
    };

    // Toggle in Service ========================================================================================================
    const toggleInService = async (uuid, currentStatus) => {
        const service_data = {
            service_uuid: uuid,
            current_status: currentStatus
        }
    
        // Call the toggle service route
        const { message, status } = await use_toggle_inService(service_data); // Implement toggle service logic
    
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        } else {
            // Update the serviceData array by toggling the inForService status
            setServiceData((prev) => 
                prev.map((event) => 
                    event.service_uuid === uuid 
                        ? { ...event, inForService: !currentStatus } // Toggle inForService
                        : event // Leave other events unchanged
                )
            );
        }
    };

    // Toggle in Service ========================================================================================================
    const toggleVor = async (uuid, currentStatus, vehicle_uuid) => {
        const service_data = {
            service_uuid: uuid,
            current_status: currentStatus,
            vehicle_uuid: vehicle_uuid
        }
    
        // Call the toggle service route
        const { message, status } = await use_toggle_vor(service_data); // Implement toggle service logic
    
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        } else {
            // Update the serviceData array by toggling the inForService status
            setServiceData((prev) => 
                prev.map((event) => 
                    event.service_uuid === uuid 
                        ? { ...event, vor: !currentStatus } // Toggle inForService
                        : event // Leave other events unchanged
                )
            );
        }
    };

    // Add Comment to service record ========================================================================================================
    const addComment = async (uuid) => {

        const comment = prompt("Enter comment:");

        const service_data = {
            service_uuid: uuid,
            comments: comment
        }
        if (!comment) return; // If no comment is entered, return

        logger.log("Add Comment Data:", service_data);

        // Call the add comment route
        const { message, status } = await use_add_comment(service_data); // Implement add comment logic
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        } else {
            // Update the serviceData array by adding the comment
            setServiceData((prev) =>
                prev.map((event) =>
                    event.service_uuid === uuid
                        ? { ...event, comments: comment } // Add comment
                        : event // Leave other events unchanged
                )
            );
        }
    };

    // Remove comment from service record ========================================================================================================
    const removeComment = async (uuid) => {
        const service_data = {
            service_uuid: uuid
        }

        logger.log("Remove Comment Data:", service_data);
        // Call the remove comment route
        const { message, status } = await use_remove_comment(service_data); // Implement remove comment logic
        if (status !== 200 && status !== 201) {
            showAlert({
                message: message,
                type: 'danger',
                buttons: [{
                    label: 'Close',
                }],
            });
        } else {
            // Update the serviceData array by removing the comment
            setServiceData((prev) =>
                prev.map((event) =>
                    event.service_uuid === uuid
                        ? { ...event, comments: null } // Remove comment
                        : event // Leave other events unchanged
                )
            );
        }
    };

    // Handle delete service record ========================================================================================================
    const handleDelete = async (uuid) => {
        // Show confirmation alert
        showAlert({
            message: 'Are you sure you want to delete this service record?',
            type: 'warning',
            buttons: [
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: () => {
                        deleteEvent(uuid);
                    },
                },
                {
                    label: 'Cancel',
                    type: 'secondary',
                },
            ],
        });
    };
    

    return (
        <div id={theme} className="calendar" style={{ backgroundColor: theme === 'dark' ? '#080808' : '#fff' }}>
            {/* Show loading spinner */}
            {isLoading && (
                <div>
                    <ActivityIndicator size={50} />
                </div>
            )}
    
            {/* Show PDF Upload Modal */}
            {showUploadPdfModal && (
                <UploadPdf serviceUuid={pdfUploadData?.uuid} type={pdfUploadData?.type} show={setShowUploadPdfModal} />
            )}

            {/* Show PDF Viewer */}
            {showPdf && (
                <PdfViewer pdfUrl={pdfUrl} show={setShowPdf} />
            )}
    
            {/* Show Service Records */}
            {!isLoading && !showUploadPdfModal && showServiceRecords && (
                <div style={{ textAlign: 'left', padding: 20, position: 'fixed', width: '65%', height: '60vh', overflowY: 'hidden' }}>
                    <h3>Service Records</h3>
                    {selectedVehicleServiceRecords.length > 0 ? (
                        <>
                            <h5>{removeUnderscore(selectedVehicleServiceRecords[0].reg)}</h5>
                            <div style={{ marginTop: '10px', overflowY: 'auto', height: 'calc(100% - 50px)' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff',
                                        color: theme === 'dark' ? '#ffffff' : '#000000',
                                        border: theme === 'dark' ? '1px solid #555' : '1px solid #ddd',
                                    }}
                                >
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, textAlign: 'center' }}>
                                        <tr style={{ backgroundColor: theme === 'dark' ? '#333' : '#f5f5f5' }}>
                                            <th style={getTableHeaderStyles(theme)}>Date</th>
                                            <th style={getTableHeaderStyles(theme)}>Company</th>
                                            <th style={getTableHeaderStyles(theme)}>PMI</th>
                                            <th style={getTableHeaderStyles(theme)}>Brakes</th>
                                            <th style={getTableHeaderStyles(theme)}>MOT</th>
                                            <th style={getTableHeaderStyles(theme)}>Tacho</th>
                                            <th style={getTableHeaderStyles(theme)}>Loler</th>
                                            <th style={getTableHeaderStyles(theme)}>Repair</th>
                                            {canEdit && (
                                                <th style={getTableHeaderStyles(theme)}>
                                                    <FontAwesomeIcon
                                                        icon={faTrashCan}
                                                        style={{ color: theme === 'dark' ? '#fff' : '#000', width: 20, cursor: 'pointer' }}
                                                    />
                                                </th>
                                            )}
                                            <th style={getTableHeaderStyles(theme)}>
                                                <FontAwesomeIcon
                                                    icon={faCircleQuestion}
                                                    size= "lg"
                                                    style={{ color: theme === 'dark' ? '#fff' : '#000', width: 20, cursor: 'pointer' }}
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedVehicleServiceRecords.map((record) => (
                                        <React.Fragment key={record.service_uuid}>
                                            <tr
                                            style={{
                                                backgroundColor:
                                                    record.inForService ?
                                                        (theme === 'dark' ? 'rgba(252, 203, 8, 0.2)' : 'rgba(252, 203, 8, 0.4)')
                                                        : new Date(record.date) < new Date() ? 'rgba(162, 162, 162, 0.20)'
                                                        : 'transparent',
                                            }}
                                            >
                                            <td style={getTableHeaderStyles(theme)}>{new Date(record.date).toLocaleDateString('en-GB')}</td>
                                            <td style={getTableHeaderStyles(theme)}>{record.service_company}</td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={checkForPdf(record, 'service') ? 'hoverable' : ''}
                                                style={getStatusCellStyles(theme, record.service_completed)}
                                                onClick={() => checkForPdf(record, 'service') ? handleOpenPdf(record, 'service') : null}
                                                >
                                                {(checkForPdf(record, 'service') || checkStatus(record, 'service') || hasEvent(record, 'service')) && <FontAwesomeIcon icon={checkForPdf(record, 'service') ? faPenToSquare : checkStatus(record, 'service') ? faCheck : faTimes} style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={checkForPdf(record, 'brakes') ? 'hoverable' : ''}
                                                style={getStatusCellStyles(theme, record.brakes_completed)}
                                                onClick={() => checkForPdf(record, 'brakes') ? handleOpenPdf(record, 'brakes') : null}
                                                >
                                                {(checkForPdf(record, 'brakes') || checkStatus(record, 'brakes') || hasEvent(record, 'brakes')) && <FontAwesomeIcon icon={checkForPdf(record, 'brakes') ? faPenToSquare : checkStatus(record, 'brakes') ? faCheck : faTimes} style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={checkForPdf(record, 'mot') ? 'hoverable' : ''}
                                                style={getStatusCellStyles(theme, record.mot_completed)}
                                                onClick={() => checkForPdf(record, 'mot') ? handleOpenPdf(record, 'mot') : null}
                                                >
                                                {(checkForPdf(record, 'mot') || checkStatus(record, 'mot') || hasEvent(record, 'mot')) && <FontAwesomeIcon icon={checkForPdf(record, 'mot') ? faPenToSquare : checkStatus(record, 'mot') ? faCheck : faTimes} style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={checkForPdf(record, 'tacho') ? 'hoverable' : ''}
                                                style={getStatusCellStyles(theme, record.tacho_completed)}
                                                onClick={() => checkForPdf(record, 'tacho') ? handleOpenPdf(record, 'tacho') : null}
                                                >
                                                {(checkForPdf(record, 'tacho') || checkStatus(record, 'tacho') || hasEvent(record, 'tacho')) && <FontAwesomeIcon icon={checkForPdf(record, 'tacho') ? faPenToSquare : checkStatus(record, 'tacho') ? faCheck : faTimes} style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={checkForPdf(record, 'loler') ? 'hoverable' : ''}
                                                style={getStatusCellStyles(theme, record.loler_completed)}
                                                onClick={() => checkForPdf(record, 'loler') ? handleOpenPdf(record, 'loler') : null}
                                                >
                                                {(checkForPdf(record, 'loler') || checkStatus(record, 'loler') || hasEvent(record, 'loler')) && <FontAwesomeIcon icon={checkForPdf(record, 'loler') ? faPenToSquare : checkStatus(record, 'loler') ? faCheck : faTimes} style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span
                                                className={''}
                                                style={{ ...getStatusCellStyles(theme, record.repair_completed), color: '#f700ffff' }}
                                                onClick={() => null}
                                                >
                                                {hasEvent(record, 'repair') && <FontAwesomeIcon icon={faScrewdriverWrench} size='sm' style={{ width: 16 }} />}
                                                </span>
                                            </td>
                                            {canEdit && (
                                                <td style={getTableHeaderStyles(theme)}>
                                                    <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <FontAwesomeIcon
                                                        className="hoverable"
                                                        icon={faTrashCan}
                                                        style={{
                                                            color: 'red',
                                                            width: 20,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handleDelete(record.service_uuid)}
                                                        />
                                                    </span>
                                                </td>
                                            )}
                                            <td style={getTableHeaderStyles(theme)}>
                                                <span style={{ display: record.comments ? 'flex' : 'none', justifyContent: 'center', alignItems: 'center' }}>
                                                <FontAwesomeIcon
                                                    className="hoverable"
                                                    icon={faCircleQuestion}
                                                    size="lg"
                                                    style={{
                                                        color: theme === 'dark' ? 'lightblue' : '#0030b0',
                                                        cursor: 'pointer',
                                                        width: 20,
                                                    }}
                                                    onClick={() =>
                                                    setExpandedCommentId(expandedCommentId === record.service_uuid ? null : record.service_uuid)
                                                    }
                                                />
                                                </span>
                                            </td>
                                            </tr>

                                            {expandedCommentId === record.service_uuid && (
                                            <tr>
                                                <td colSpan={10} style={ getTableHeaderStyles(theme) }>
                                                    <span style={{ fontStyle: 'italic', color: theme === 'dark' ? '#ccc' : '#333' }}>
                                                        {record.comments}
                                                    </span>
                                                </td>
                                            </tr>
                                            )}
                                        </React.Fragment>
                                        ))}

                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <h5>No data for this vehicle</h5>
                    )}
                </div>
            )}
    
            {/* Default Calendar View */}
            {!isLoading && !showUploadPdfModal && !showServiceRecords && (
                <>
                    <div className="header">
                        <button onClick={() => setCurrentDate(subMonths(currentDate, 1))}>&lt;</button>
                        <h2>{format(currentDate, "MMMM yyyy")}</h2>
                        <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}>&gt;</button>
                    </div>
                    <div className="service-colour-key" aria-label="Service colour key">
                        {[
                            { type: 'pmi', label: 'PMI' },
                            { type: 'mot', label: 'MOT' },
                            { type: 'repair', label: 'Repair' },
                            { type: 'loler', label: 'LOLER' },
                            { type: 'tacho', label: 'Tacho' },
                        ].map((indicator) => (
                            <span className="service-colour-key-item" key={indicator.type}>
                                <span className={`event-indicator event-indicator-${indicator.type}`} />
                                {indicator.label}
                            </span>
                        ))}
                    </div>
                    <div className="day-names">
                        <span>Sun</span>
                        <span>Mon</span>
                        <span>Tue</span>
                        <span>Wed</span>
                        <span>Thu</span>
                        <span>Fri</span>
                        <span>Sat</span>
                    </div>
                    <AnimatePresence mode="wait" style={{ zIndex: 0, position: 'static' }}>
                        <motion.div
                            key={currentDate}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            className="days-grid"
                            style={{ zIndex: 0, position: 'static' }}
                        >
                            {generateCalendar()}
                        </motion.div>
                    </AnimatePresence>
                    <AnimatePresence>{selectedDate && renderDayView()}</AnimatePresence>
                </>
            )}
    
            {/* Add Service Modal */}
            {openModal && (
                <AddServiceModal
                    theme={theme}
                    isOpen={openModal}
                    onClose={() => setOpenModal(false)}
                    onSubmit={(data) => handleAddService(data)}
                    vehicles={allVehicles}
                    serviceContacts={serviceCompanies}
                />
            )}

            {/* MOT Date Selector Modal for Rescheduling */}
            {showMotDateSelector && (
                <div id={theme} className="overlay">
                    <div className="modal-container" style={{ maxWidth: '400px', padding: '24px' }}>
                        <h2 style={{ marginBottom: '16px' }}>Select New MOT Date</h2>
                        <p style={{ fontSize: '14px', marginBottom: '20px', opacity: 0.8 }}>
                            Rescheduling an event with an MOT requires manually scheduling the new MOT date. Please select a date below.
                        </p>
                        <div className="input-group" style={{ marginBottom: '24px' }}>
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>MOT Date</label>
                            <input
                                type="date"
                                className="input"
                                value={selectedMotDate}
                                onChange={(e) => setSelectedMotDate(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: '1px solid #ccc',
                                    backgroundColor: theme === 'dark' ? '#1e2535' : '#fff',
                                    color: theme === 'dark' ? '#fff' : '#000'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button 
                                className="btn-secondary" 
                                onClick={() => {
                                    setShowMotDateSelector(false);
                                    setMotEventForSelector(null);
                                    fetchData(); // refresh
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={handleConfirmMotDate}
                                disabled={!selectedMotDate}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    backgroundColor: '#0588f3',
                                    color: '#fff',
                                    opacity: !selectedMotDate ? 0.6 : 1
                                }}
                            >
                                Confirm & Add
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
    
};

export default AdvancedCalendar;
